interface NativeSession { uid?: string; idToken?: string }
interface NativeHandler { postMessage(message: { action: string; kind?: string }): Promise<unknown> }

function nativeHandler(): NativeHandler | undefined {
  if (typeof window === "undefined" || window.top !== window || window.location.origin !== "https://www.banculem.com.mx") return undefined;
  return (window as Window & { webkit?: { messageHandlers?: { banculem?: NativeHandler } } }).webkit?.messageHandlers?.banculem;
}

export function isNativeBanculem(): boolean { return Boolean(nativeHandler()); }

export async function nativeSession(interactive: boolean): Promise<NativeSession> {
  const handler = nativeHandler();
  if (!handler) throw new Error("La conexión con la app no está disponible.");
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    const value = await Promise.race([
      handler.postMessage({ action: interactive ? "signIn" : "restore" }),
      new Promise<never>((_, reject) => { timer = setTimeout(() => reject(new Error("La conexión con Google tardó demasiado. Vuelve a intentar.")), interactive ? 120_000 : 20_000); }),
    ]);
    if (!value || typeof value !== "object" || Array.isArray(value)) throw new Error("No se pudo verificar la sesión.");
    const session = value as NativeSession;
    if (session.uid !== undefined && (typeof session.uid !== "string" || !session.uid)) throw new Error("Sesión no válida.");
    if (session.idToken !== undefined && (typeof session.idToken !== "string" || !session.idToken || !session.uid)) throw new Error("Sesión no válida.");
    return { uid: session.uid, idToken: session.idToken };
  } finally { clearTimeout(timer); }
}

export async function openNativeWidgets(): Promise<void> {
  await nativeHandler()?.postMessage({ action: "widgets" });
}

const HAPTICS_KEY = "banculem-native-haptics";
export function nativeHapticsEnabled(): boolean {
  try { return window.localStorage.getItem(HAPTICS_KEY) !== "false"; } catch { return true; }
}
export function setNativeHapticsEnabled(enabled: boolean): void {
  try { window.localStorage.setItem(HAPTICS_KEY, String(enabled)); } catch { /* Optional preference. */ }
}

/** Optional device feedback must never reject or interrupt a financial operation. */
export async function nativeHaptic(kind: "selection" | "success", force = false): Promise<boolean> {
  if (kind !== "selection" && kind !== "success") return false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    const handler = nativeHandler();
    if (!handler || (!force && !nativeHapticsEnabled())) return false;
    const result = await Promise.race([
      handler.postMessage({ action: "haptic", kind }),
      new Promise<null>(resolve => { timer = setTimeout(() => resolve(null), 1500); }),
    ]);
    return Boolean(result && typeof result === "object" && (result as { ok?: unknown }).ok === true);
  } catch { return false; } finally { clearTimeout(timer); }
}

export type NativeNotificationCommand = "Status" | "Enable" | "Disable" | "Refresh" | "Test" | "Confirm";
export interface NativeNotificationState { state: "setup" | "denied" | "error" | "enabled" | "pending" | "disabled" | "stale"; enabled: boolean; configured: boolean; mode?: "local"; pendingCount?: number; updatedAt?: string }
export async function nativeNotificationCommand(command: NativeNotificationCommand, kind?: "expense" | "period"): Promise<Record<string, unknown>> {
  const handler = nativeHandler();
  if (!handler) throw new Error("Abre la app instalada de Banculem.");
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    const value = await Promise.race([
      handler.postMessage({ action: `notifications${command}`, ...(kind ? { kind } : {}) }),
      new Promise<never>((_, reject) => { timer = setTimeout(() => reject(new Error("La conexión tardó demasiado. Revisa tu conexión y vuelve a intentar.")), 30_000); }),
    ]);
    if (!value || typeof value !== "object" || Array.isArray(value)) throw new Error("Actualiza la app instalada para configurar los avisos.");
    return value as Record<string, unknown>;
  } finally { clearTimeout(timer); }
}
